import React, { useEffect, useMemo, useState } from 'react';
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined';
import moment from 'moment';
import ReactDatePicker from 'react-datepicker';
import Select, { ValueType } from 'react-select';

import Button, {
  ButtonSize,
  ButtonType
} from '../shared/components/core-ui/button/button.component';
import { birdToast } from '../shared/components/core-ui/toast';
import { isErrorInfo } from '../shared/interfaces/error-info.interface';
import { getAllProperties } from '../PropertyModule/shared/services/data.service';
import { getAllAreas } from '../shared/services/data/lookups.data';
import { PropertyUiModel } from '../shared/ui-models/property.ui-model';
import { calculateUnitAvailabilityDays, UnitAvailabilityDaysPayload } from './website-sort.service';

const MODULE_NAME = 'WEBSITE_SORT_MODULE';

type SelectOption = {
  value: string;
  label: string;
};

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: 58,
    borderColor: state.isFocused ? '#00875a' : '#d9d9d9',
    borderRadius: 8,
    boxShadow: state.isFocused ? '0 0 0 1px #00875a' : 'none',
    cursor: 'pointer',
    '&:hover': { borderColor: '#00875a' }
  }),
  valueContainer: (base: any) => ({ ...base, padding: '2px 18px' }),
  placeholder: (base: any) => ({ ...base, color: '#8b8b93' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base: any) => ({ ...base, color: '#37454d', paddingRight: 14 }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
};

export default function WebsiteSortContainer() {
  const [areas, setAreas] = useState<SelectOption[]>([]);
  const [properties, setProperties] = useState<PropertyUiModel[]>([]);
  const [areaId, setAreaId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const propertyOptions = useMemo<SelectOption[]>(
    () =>
      properties
        .filter((property) => !areaId || property.areaId === areaId)
        .map((property) => ({ value: property.id || '', label: property.name || '' }))
        .filter((property) => property.value && property.label),
    [areaId, properties]
  );

  useEffect(() => {
    let ignore = false;

    Promise.all([getAllAreas(MODULE_NAME), getAllProperties()])
      .then(([areasResponse, propertiesResponse]) => {
        if (ignore) return;

        if (isErrorInfo(areasResponse)) {
          birdToast.error('Unable to load areas. Please try again.', 'Loading failed');
        } else {
          setAreas(
            areasResponse
              .map((area) => ({ value: area.id || '', label: area.name }))
              .filter((area) => area.value && area.label)
          );
        }

        if (isErrorInfo(propertiesResponse)) {
          birdToast.error('Unable to load projects. Please try again.', 'Loading failed');
        } else {
          setProperties(propertiesResponse);
        }
      })
      .catch(() => {
        if (!ignore) {
          birdToast.error('Unable to load filter options. Please try again.', 'Loading failed');
        }
      })
      .finally(() => {
        if (!ignore) setLoadingOptions(false);
      });

    return () => {
      ignore = true;
    };
  }, []);

  const handleAreaChange = (option: ValueType<SelectOption, false>) => {
    const nextAreaId = option?.value || '';
    setAreaId(nextAreaId);

    if (nextAreaId) setPropertyId('');
  };

  const handlePropertyChange = (option: ValueType<SelectOption, false>) => {
    const nextPropertyId = option?.value || '';
    setPropertyId(nextPropertyId);

    if (nextPropertyId) setAreaId('');
  };

  const handleReset = () => {
    setAreaId('');
    setPropertyId('');
    setFrom(null);
    setTo(null);
  };

  const handleSubmit = async () => {
    if (!areaId && !propertyId) {
      birdToast.warning('Please select an area or project.');
      return;
    }

    if (!from || !to) {
      birdToast.warning('Please select both From and To dates.');
      return;
    }

    if (to.getTime() < from.getTime()) {
      birdToast.warning('The To date must be on or after the From date.');
      return;
    }

    const payload: UnitAvailabilityDaysPayload = {
      from: moment(from).format('YYYY-MM-DD'),
      to: moment(to).format('YYYY-MM-DD'),
      ...(areaId ? { areaId } : {}),
      ...(propertyId ? { propertyId } : {})
    };

    setSubmitting(true);

    try {
      const response = await calculateUnitAvailabilityDays(payload);

      if (isErrorInfo(response)) {
        birdToast.error(response.errorMessage || 'Unable to apply filters.');
        return;
      }

      birdToast.success(
        `${response.message}. ${response.data.upserted} of ${response.data.processedRooms} rooms updated.`
      );
    } catch {
      birdToast.error('Unable to apply filters. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedArea = areas.find((area) => area.value === areaId) || null;
  const selectedProperty =
    propertyOptions.find((property) => property.value === propertyId) || null;

  return (
    <main className="min-h-screen bg-creams-50 px-4 py-8 sm:px-6 lg:px-10 lg:py-12">
      <div className="mx-auto w-full max-w-[1600px]">
        <h1 className="mb-8 font-recoleta text-4xl font-semibold text-neutral-500 sm:text-5xl lg:mb-12 lg:text-6xl">
          Website Sort
        </h1>

        <section
          className="rounded-2xl border border-gray-200 bg-white p-5 shadow-[0_8px_28px_rgba(55,69,77,0.08)] sm:p-7 lg:p-9"
          aria-label="Unit availability filters"
        >
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-12 xl:items-end">
            <div className="xl:col-span-3">
              <label
                className="mb-3 block text-lg font-gilroy-medium text-neutral-500"
                htmlFor="area-filter"
              >
                Area
              </label>
              <Select
                inputId="area-filter"
                value={selectedArea}
                options={areas}
                onChange={handleAreaChange}
                placeholder={loadingOptions ? 'Loading...' : 'Search...'}
                isClearable
                isSearchable
                isDisabled={loadingOptions || submitting}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="xl:col-span-3">
              <label
                className="mb-3 block text-lg font-gilroy-medium text-neutral-500"
                htmlFor="project-filter"
              >
                Project
              </label>
              <Select
                inputId="project-filter"
                value={selectedProperty}
                options={propertyOptions}
                onChange={handlePropertyChange}
                placeholder="Search..."
                isClearable
                isSearchable
                isDisabled={loadingOptions || submitting}
                noOptionsMessage={() => 'No projects found'}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="xl:col-span-2">
              <label
                className="mb-3 block text-lg font-gilroy-medium text-neutral-500"
                htmlFor="from-filter"
              >
                From
              </label>
              <div className="relative">
                <ReactDatePicker
                  id="from-filter"
                  selected={from}
                  onChange={(date: Date | null) => {
                    setFrom(date);
                    if (date && to && to.getTime() < date.getTime()) setTo(null);
                  }}
                  selectsStart
                  startDate={from}
                  endDate={to}
                  maxDate={to || undefined}
                  dateFormat="MMM d, yyyy"
                  placeholderText="Select date"
                  dayClassName={() => '!w-[1.8em] !leading-[1.8em] !text-[1.4rem]'}
                  weekDayClassName={() => '!w-[1.8em] !leading-[1.8em] !text-[1.3rem]'}
                  className="!h-[58px] !min-h-[58px] w-full rounded-lg border border-gray-200 px-3 pr-14 text-[1.4rem] font-gilroy-bold leading-[58px] outline-none transition-shadow duration-200 placeholder:text-neutral-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  calendarClassName="[&_.react-datepicker__current-month]:!text-[1.6rem] [&_.react-datepicker__current-month]:!font-gilroy-bold [&_.react-datepicker__current-month]:!leading-[2rem] [&_.react-datepicker__current-month]:!text-neutral-800 [&_.react-datepicker-year-header]:!text-[1.6rem] [&_.react-datepicker-year-header]:!font-gilroy-bold"
                  wrapperClassName="block !h-[58px] w-full [&_.react-datepicker__input-container]:!h-[58px]"
                  popperClassName="z-[60]"
                  disabled={submitting}
                />
                <CalendarTodayOutlinedIcon className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400" />
              </div>
            </div>

            <div className="xl:col-span-2">
              <label
                className="mb-3 block text-lg font-gilroy-medium text-neutral-500"
                htmlFor="to-filter"
              >
                To
              </label>
              <div className="relative">
                <ReactDatePicker
                  id="to-filter"
                  selected={to}
                  onChange={(date: Date | null) => setTo(date)}
                  selectsEnd
                  startDate={from}
                  endDate={to}
                  minDate={from || undefined}
                  dateFormat="MMM d, yyyy"
                  placeholderText="Select date"
                  dayClassName={() => '!w-[1.8em] !leading-[1.8em] !text-[1.4rem]'}
                  weekDayClassName={() => '!w-[1.8em] !leading-[1.8em] !text-[1.3rem]'}
                  className="!h-[58px] !min-h-[58px] w-full rounded-lg border border-gray-200 px-3 pr-14 text-[1.4rem] font-gilroy-bold leading-[58px] outline-none transition-shadow duration-200 placeholder:text-neutral-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  calendarClassName="[&_.react-datepicker__current-month]:!text-[1.6rem] [&_.react-datepicker__current-month]:!font-gilroy-bold [&_.react-datepicker__current-month]:!leading-[2rem] [&_.react-datepicker__current-month]:!text-neutral-800 [&_.react-datepicker-year-header]:!text-[1.6rem] [&_.react-datepicker-year-header]:!font-gilroy-bold"
                  wrapperClassName="block !h-[58px] w-full [&_.react-datepicker__input-container]:!h-[58px]"
                  popperClassName="z-[60]"
                  disabled={submitting}
                />
                <CalendarTodayOutlinedIcon className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400" />
              </div>
            </div>

            <div className="flex flex-wrap gap-3 md:col-span-2 xl:col-span-2 xl:flex-nowrap">
              <Button
                onClick={handleSubmit}
                size={ButtonSize.small}
                type={ButtonType.primary}
                loading={submitting}
                disabled={submitting || loadingOptions}
                className="flex-1"
                classNameButton="h-[58px] w-full whitespace-nowrap px-5"
              >
                Apply filters
              </Button>
              <Button
                onClick={handleReset}
                size={ButtonSize.small}
                type={ButtonType.primaryInverted}
                disabled={submitting}
                className="flex-1"
                classNameButton="h-[58px] w-full px-5 !text-primary-600"
              >
                Reset
              </Button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
